import React, { useState, useRef, useEffect } from 'react';
import { Box, Container, Typography, Paper, TextField, Button, Stack, Alert, Snackbar, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import Grid from '@mui/material/Grid';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ShieldIcon from '@mui/icons-material/Shield';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Why from './WhyPage';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LayersIcon from '@mui/icons-material/Layers';
import GroupIcon from '@mui/icons-material/Group';
import type { SxProps } from '@mui/material';
import Header from './Header';
import Footer from './Footer';
import ReputationPage from './ReputationPage';
import ParentsPage from './ParentsPage';
import ComparePage from './ComparePage';
import BlogPage from './BlogPage';
import BlogCostPage from './blog/BlogCostPage';
import BlogCamerasPage from './blog/BlogCamerasPage';
import BlogPreventPage from './blog/BlogPreventPage';
import BlogEmotionalAbusePage from './blog/BlogEmotionalAbusePage';
import EnterprisePage from './EnterprisePage';
import InsurancePartnerPage from './InsurancePartnerPage';
import UseCasesPage from './UseCasesPage';
import PrivacyPage from './PrivacyPage';
import BlogGuziejkaPage from './blog/BlogGuziejkaPage';
import BlogSunyCortlandPage from './blog/BlogSunyCortlandPage';
import BlogDesPlainePage from './blog/BlogDesPlainePage';
import BlogHarveyMucklebustPage from './blog/BlogHarveyMucklebustPage';
import BlogDisputedAllegationsPage from './blog/BlogDisputedAllegationsPage';
import BlogSexualPredatorsPage from './blog/BlogSexualPredatorsPage';
import BlogArizonaCoolidgePage from './blog/BlogArizonaCoolidgePage';
import ExamplesPage from './ExamplesPage';
import IntegrationsPage from './IntegrationsPage';
import GovernmentPage from './GovernmentPage';
import CameraPartnersPage from './CameraPartnersPage';
import LpDaycareOwner from './lp/LpDaycareOwner';
import LpEnterpriseCOO from './lp/LpEnterpriseCOO';
import LpInsurance from './lp/LpInsurance';
import LpGovernment from './lp/LpGovernment';
import LpCameraPartner from './lp/LpCameraPartner';
import LpAppPartner from './lp/LpAppPartner';
import ForStaffPage from './ForStaffPage';
import AboutPage from './AboutPage';
import CookieConsentBanner from './CookieConsentBanner';
import PrivacyPolicyPage from './PrivacyPolicyPage';
import SEO from './SEO';
import { Helmet } from 'react-helmet-async';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FavoriteOutlinedIcon from '@mui/icons-material/FavoriteBorder';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { trackPageView, trackEvent, trackFormSubmission, trackScrollDepth, trackTimeOnPage, trackFormView, trackFormStart, trackFormAttempt, trackFormError, trackFormAbandon, trackKeySection, setVisitorTypeDimension, incrementSessionDepth } from './analytics';

// ── Scroll-fade animation hook ──────────────────────────────────────────────
function useScrollFade(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);
  return { ref, inView };
}

const fadeStyle = (inView: boolean, delay = 0): SxProps => ({
  opacity: inView ? 1 : 0,
  transform: inView ? 'translateY(0)' : 'translateY(32px)',
  transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
});

// ── CountUp helper ───────────────────────────────────────────────────────────
function useCountUp(target: number, active: boolean, duration = 1500) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setValue(start);
      if (start >= target) clearInterval(timer);
    }, 16);
    return () => clearInterval(timer);
  }, [active, target, duration]);
  return value;
}

// ── Contact Form ─────────────────────────────────────────────────────────────
interface ContactFormProps {
  sx?: SxProps;
  onSuccess?: () => void;
  pagePath?: string;
}

function ContactForm({ sx, onSuccess, pagePath }: ContactFormProps) {
  const [formData, setFormData] = useState({ name: '', organization: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const hasTrackedView = useRef(false);
  const hasTrackedStart = useRef(false);
  const path = pagePath ?? window.location.pathname;

  // Fire form_view once when the form scrolls into view
  useEffect(() => {
    const el = formRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasTrackedView.current) {
          hasTrackedView.current = true;
          trackFormView(path);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [path]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFirstFocus = () => {
    if (!hasTrackedStart.current) {
      hasTrackedStart.current = true;
      trackFormStart(path);
    }
  };

  // Track form abandon on page unload if user started but didn't submit
  useEffect(() => {
    const handleUnload = () => {
      if (!hasTrackedStart.current) return;
      const filled = Object.values(formData).filter(v => v.trim().length > 0).length;
      trackFormAbandon(path, filled);
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [path, formData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    trackFormAttempt(path);
    setIsSubmitting(true);
    try {
      const response = await fetch('https://us-central1-smart-media-clarity.cloudfunctions.net/handleForm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...formData, timestamp: new Date().toISOString(), source: 'landing-page' }),
      });
      if (response.ok) {
        setShowSuccess(true);
        setFormData({ name: '', organization: '', email: '', phone: '' });
        hasTrackedStart.current = false; // prevent false abandon event after success
        trackFormSubmission(path);
        if (onSuccess) onSuccess();
      } else {
        setShowError(true);
        trackFormError(path);
      }
    } catch {
      setShowError(true);
      trackFormError(path);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Paper ref={formRef} elevation={0} sx={{ p: { xs: 3, md: 5 }, bgcolor: '#13131f', borderRadius: 3, ...sx }}>
      <Typography variant="h4" fontWeight={800} gutterBottom sx={{ color: '#f0f0f8', fontFamily: 'Inter, system-ui, sans-serif', mb: 0.5 }}>
        Book Your Demo
      </Typography>
      <Typography variant="body1" sx={{ color: '#9499b0', mb: 3, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15 }}>
        See Smart Media Clarity in action — 30-minute personalized walkthrough.
      </Typography>
      <Box component="form" noValidate autoComplete="off" onSubmit={handleSubmit} onFocus={handleFirstFocus}>
        <Stack spacing={2.5}>
          <TextField label="Full Name" name="name" value={formData.name} onChange={handleInputChange} fullWidth required />
          <TextField label="Organization" name="organization" value={formData.organization} onChange={handleInputChange} fullWidth required />
          <TextField label="Work Email" name="email" type="email" value={formData.email} onChange={handleInputChange} fullWidth required />
          <TextField label="Phone (optional)" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} fullWidth />
          <Button
            variant="contained"
            color="primary"
            size="large"
            type="submit"
            disabled={isSubmitting}
            sx={{ fontWeight: 700, fontSize: 16, borderRadius: 2, py: 1.5, color: '#0a0a12', bgcolor: '#5ce1e6', '&:hover': { bgcolor: '#4ecfd4' } }}
          >
            {isSubmitting ? 'Sending…' : 'Book a Demo →'}
          </Button>
        </Stack>
      </Box>
      <Snackbar open={showSuccess} autoHideDuration={6000} onClose={() => setShowSuccess(false)}>
        <Alert onClose={() => setShowSuccess(false)} severity="success" sx={{ width: '100%' }}>
          Thank you! We'll be in touch within one business day.
        </Alert>
      </Snackbar>
      <Snackbar open={showError} autoHideDuration={6000} onClose={() => setShowError(false)}>
        <Alert onClose={() => setShowError(false)} severity="error" sx={{ width: '100%' }}>
          Something went wrong — please try again or email us directly.
        </Alert>
      </Snackbar>
    </Paper>
  );
}

// ── Contact Us Page ───────────────────────────────────────────────────────────
function ContactUsPage() {
  const { pathname } = useLocation();
  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Book a Demo — See SMCLA in 30 Minutes" description="Book a personalized walkthrough of SMCLA's childcare AI platform. See real-time detection, the investigation workflow, and multi-site dashboard live." path="/contact" />
      <Header />
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Typography variant="h3" fontWeight={800} align="center" sx={{ color: '#f0f0f8', mb: 1, fontFamily: 'Inter, system-ui, sans-serif', letterSpacing: '-0.02em' }}>
          Book a Demo
        </Typography>
        <Typography variant="body1" align="center" sx={{ mb: 5, fontSize: 16, color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif' }}>
          We're running a pilot program with limited spots. Book a 30-minute walkthrough and see the AI in action.
        </Typography>
        <ContactForm pagePath={pathname} />
      </Container>
      <Footer />
    </Box>
  );
}

// ── Feature card data ─────────────────────────────────────────────────────────
const features = [
  {
    icon: <LayersIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Audio + Visual AI Detection',
    body: 'Detects physical aggression, abnormal contact, and suspicious behavior across both video and audio — purpose-built for childcare.',
  },
  {
    icon: <VolumeUpIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Emotional Abuse Detection',
    body: 'AI analyzes vocal tone, raised voices, verbal threats, and patterns of humiliation — the abuse that cameras alone can never detect. Alerts sent in real time.',
  },
  {
    icon: <CheckCircleIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Multi-Site Owner Alerts',
    body: 'One dashboard, all locations. Real-time push notifications — you know about an incident before your managers do.',
  },
  {
    icon: <CameraAltIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Works With Your Cameras',
    body: 'Installs on existing cameras — no expensive upgrades. No cameras? We provide and set up everything for full coverage.',
  },
  {
    icon: <GroupIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Staff Protection Built In',
    body: 'Timestamped, tamper-evident clips protect innocent staff from false accusations — and provide legal-grade evidence when needed.',
  },
  {
    icon: <ManageSearchIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />,
    title: 'Full-Facility Incident Reconstruction',
    body: 'When an alert is confirmed as a true incident, SMCLA opens an investigation and pulls 24 hours of footage from every camera in the facility — not just the one that triggered the alert. Reconstruct exactly what happened, when, and where.',
  },
];

const trustItems = [
  { title: 'Responsible audio analysis', body: 'Audio is analyzed for emotional abuse detection — processed in real time, not stored as a raw recording. We guide you through state consent requirements during setup.' },
  { title: 'Restricted access', body: 'Only the facility owner and explicitly authorized personnel can view flagged clips — each clip, per role. Access is never granted in bulk. Every view is logged with a timestamp and user identity.' },
  { title: 'Snippet-only transmission', body: 'Only the small flagged alert clips leave the facility — never full footage streams. Full recordings stay local, are retained for 48 hours, then permanently and irrecoverably destroyed if unused.' },
  { title: 'End-to-end encryption', body: 'All data is encrypted in transit and at rest. Clips include chain-of-custody metadata that makes them admissible in court.' },
];

const whyItems = [
  'Quickly resolve allegations with timestamped, tamper-evident video evidence',
  'Detect behavioral patterns before a single incident becomes a pattern of abuse',
  'Safeguard your daycare\'s reputation and community trust proactively',
  'Support and protect innocent staff from false accusations immediately',
];

type CellValue = 'yes' | 'no' | 'partial';
interface CompareRow { feature: string; smcla: CellValue; others: CellValue; }
const compareRows: CompareRow[] = [
  { feature: 'Physical abuse detection (AI)', smcla: 'yes', others: 'no' },
  { feature: 'Behavioral pattern recognition', smcla: 'yes', others: 'no' },
  { feature: 'Multi-site owner alerts', smcla: 'yes', others: 'partial' },
  { feature: 'Staff false-accusation protection', smcla: 'yes', others: 'no' },
  { feature: 'Legal-grade clip export', smcla: 'yes', others: 'partial' },
  { feature: 'Emotional abuse detection (audio AI)', smcla: 'yes', others: 'no' },
  { feature: 'Under 60-second alert', smcla: 'yes', others: 'no' },
];

function CompareCell({ value }: { value: CellValue }) {
  if (value === 'yes') return <CheckIcon sx={{ color: '#5ce1e6', fontSize: 20 }} />;
  if (value === 'partial') return <RemoveIcon sx={{ color: '#9499b0', fontSize: 20 }} />;
  return <CloseIcon sx={{ color: 'rgba(148,153,176,0.3)', fontSize: 20 }} />;
}

const faqItems = [
  { q: 'Does the system record audio?', a: 'Yes — for emotional abuse detection. SMCLA analyzes vocal patterns to detect verbal threats, shouting, humiliation, and degrading language directed at children. Audio is processed in real time; we guide you through consent requirements for your state during setup.' },
  { q: 'Who can see the flagged clips?', a: 'Only the facility owner and personnel you explicitly authorize, on a per-clip basis — not bulk access to a footage library. Every view is logged with the viewer\'s identity, timestamp, and action taken. Clips are small flagged snippets, not continuous recordings. Full footage streams never leave the facility.' },
  { q: 'How long is footage kept?', a: 'Full facility footage is retained for exactly 48 hours, then permanently and irrecoverably destroyed — it cannot be recovered after deletion. Footage that triggers no alert and is never needed for investigation is automatically purged. If footage is part of an active investigation or audit hold, it is preserved for as long as the case requires. We do not archive, sell, or analyze non-flagged footage for any purpose.' },
  { q: 'Will this replace my existing cameras?', a: 'No. SMCLA integrates with what you already have. If you need cameras, we supply and install them as part of a turnkey setup.' },
  { q: 'How quickly does the AI flag an incident?', a: 'Real-time analysis with push alerts sent to the owner within 60 seconds of a detected behavioral event — before it can escalate.' },
  { q: 'Is the footage admissible in court?', a: 'Yes. Every flagged clip includes tamper-evident timestamps and chain-of-custody metadata that meet evidentiary standards in civil and criminal proceedings.' },
  { q: 'I have 8 locations. Can I manage them all in one place?', a: 'Yes. One dashboard, all locations. Real-time alerts are unified — you\'ll see everything across your portfolio and can drill down per location in seconds.' },
];

// ── Main Landing Page ─────────────────────────────────────────────────────────
function Landing() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const contactFormRef = useRef<HTMLDivElement | null>(null);
  const handleBookDemo = () => {
    trackEvent('cta_click', 'Book a Demo', pathname);
    contactFormRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const statsSection = useScrollFade(0.3);
  const featuresSection = useScrollFade();
  const productDemoSection = useScrollFade();
  const compareSection = useScrollFade();
  const investigationSection = useScrollFade();
  const trustSection = useScrollFade();
  const quoteSection = useScrollFade();
  const parentSection = useScrollFade();
  const whySection = useScrollFade();
  const riskSection = useScrollFade();
  const opsSection = useScrollFade();
  const faqSection = useScrollFade();
  const demoSection = useScrollFade();

  const count500 = useCountUp(500, statsSection.inView);
  const count999 = useCountUp(999, statsSection.inView);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map(({ q, a }) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };

  const orgJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Smart Media Clarity',
    url: 'https://intro.smartmediaclarity.com',
    logo: 'https://intro.smartmediaclarity.com/logo.png',
    description: 'AI-powered child safety platform for daycare operators',
    contactPoint: { '@type': 'ContactPoint', contactType: 'sales', email: 'admin@smartmediaclarity.com' },
  };

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO
        title="AI-Powered Child Safety for Daycare"
        description="The only AI platform trained to detect physical and emotional abuse by childcare workers in real time — across every room, every location."
        path="/"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(orgJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <Header onBookDemo={handleBookDemo} />

      {/* ── Hero ── */}
      <Box sx={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', bgcolor: '#0a0a12', textAlign: 'center', px: 3, pt: 4, pb: 8 }}>
        <Box sx={{ position: 'absolute', top: '-15%', left: '-10%', width: 600, height: 600, borderRadius: '50%', background: 'radial-gradient(circle, rgba(92,225,230,0.18) 0%, transparent 70%)', filter: 'blur(40px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', bottom: '-10%', right: '-10%', width: 700, height: 700, borderRadius: '50%', background: 'radial-gradient(circle, rgba(140,82,255,0.15) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />
        <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 900, width: '100%' }}>
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.2)', borderRadius: 10, px: 2, py: 0.75, mb: 4 }}>
            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 12, color: '#5ce1e6', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              AI-Powered Child Safety — Visual Detection Only
            </Typography>
          </Box>
          <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '2.5rem', sm: '3.5rem', md: '4.5rem', lg: '5rem' }, letterSpacing: '-0.03em', lineHeight: 1.05, color: '#f0f0f8', mb: 3 }}>
            Protect children.<br /><Box component="span" sx={{ color: '#5ce1e6' }}>Build trust.</Box>
          </Typography>
          <Typography variant="h6" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: { xs: 16, md: 20 }, color: '#9499b0', maxWidth: 580, mx: 'auto', mb: 5, lineHeight: 1.6 }}>
            The only AI platform trained specifically to detect physical abuse and suspicious behavior by childcare workers — in real time, across every room, every location.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" sx={{ mb: 8 }}>
            <Button variant="contained" size="large" onClick={handleBookDemo} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 16, bgcolor: '#5ce1e6', color: '#0a0a12', borderRadius: 2, boxShadow: '0 0 24px rgba(92,225,230,0.25)', '&:hover': { bgcolor: '#4ecfd4', boxShadow: '0 0 32px rgba(92,225,230,0.35)' } }}>
              Book a Demo →
            </Button>
            <Button variant="outlined" size="large" onClick={() => { trackEvent('cta_click', 'See Real Cases', pathname); navigate('/why'); }} sx={{ px: 4, py: 1.5, fontWeight: 600, fontSize: 16, color: '#f0f0f8', borderColor: 'rgba(255,255,255,0.2)', borderRadius: 2, '&:hover': { borderColor: 'rgba(255,255,255,0.4)', bgcolor: 'rgba(255,255,255,0.05)' } }}>
              See Real Cases
            </Button>
          </Stack>
          <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.2)', boxShadow: '0 0 60px rgba(92,225,230,0.08)', bgcolor: '#13131f' }}>
            <video src="/hero-video.mp4" autoPlay loop muted playsInline style={{ width: '100%', maxHeight: 380, objectFit: 'cover', display: 'block', opacity: 0.85 }}>
              Sorry, your browser does not support embedded videos.
            </video>
          </Box>
        </Box>
      </Box>

      {/* ── Social proof strip ── */}
      <Box ref={statsSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: 3, ...fadeStyle(statsSection.inView) }}>
        <Container maxWidth="md">
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-around" alignItems="center" spacing={{ xs: 3, sm: 0 }} divider={<Box sx={{ width: 1, height: 40, bgcolor: 'rgba(255,255,255,0.08)', display: { xs: 'none', sm: 'block' } }} />}>
            {[{ num: `${count500}+`, label: 'Facilities Protected' }, { num: `${(count999 / 10).toFixed(1)}%`, label: 'Uptime SLA' }, { num: '24/7', label: 'AI Monitoring' }, { num: 'SOC 2', label: 'Security Ready' }].map(({ num, label }) => (
              <Box key={label} sx={{ textAlign: 'center' }}>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: 22, md: 26 }, color: '#5ce1e6', lineHeight: 1 }}>{num}</Typography>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: '#9499b0', mt: 0.5 }}>{label}</Typography>
              </Box>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Features ── */}
      <Box ref={featuresSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(featuresSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>What SMCLA Does</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1, letterSpacing: '-0.02em' }}>
            Purpose-built for childcare protection
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 6, maxWidth: 500, mx: 'auto' }}>
            Not a generic camera platform. Not a supervision logger. A behavioral AI trained specifically on childcare workers.
          </Typography>
          <Grid container spacing={2.5}>
            {features.map(({ icon, title, body }, i) => (
              <Grid key={title} size={{ xs: 12, md: 6 }}>
                <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, p: 3.5, height: '100%', transition: 'all 0.2s ease', '&:hover': { borderColor: 'rgba(92,225,230,0.3)', transform: 'translateY(-4px)', boxShadow: '0 20px 40px rgba(92,225,230,0.08)' }, ...fadeStyle(featuresSection.inView, i * 0.1) }}>
                  <Box sx={{ display: 'inline-flex', p: 1.25, bgcolor: 'rgba(92,225,230,0.08)', borderRadius: 2, mb: 2 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: '#f0f0f8', mb: 1 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* ── Product in Action ── */}
      <Box ref={productDemoSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(productDemoSection.inView) }} onMouseEnter={() => { if (productDemoSection.inView) trackKeySection('Product in Action', pathname); }}>
        <Container maxWidth="lg">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>Product in Action</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            Smart enough to know the difference.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 2, maxWidth: 580, mx: 'auto', lineHeight: 1.65 }}>
            SMCLA doesn't just detect crying or count heads — it reads context. A child who cries and gets immediate care is not an alert. A child who cries unnoticed for minutes is. Real CCTV footage from daycare facilities.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: 'rgba(148,153,176,0.6)', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, mb: 7, maxWidth: 520, mx: 'auto' }}>
            Footage used with facility permission. Faces blurred for privacy.
          </Typography>
          <Grid container spacing={3} alignItems="stretch">
            {/* Video 1 — Healthy interaction, no alert */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(74,222,128,0.2)', borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', ...fadeStyle(productDemoSection.inView, 0) }}>
                <Box sx={{ position: 'relative' }}>
                  <video
                    src="/high_resolution_daycare_cctv_c.mp4"
                    controls
                    muted
                    playsInline
                    style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block', bgcolor: '#0a0a12' } as React.CSSProperties}
                  >
                    Your browser does not support embedded videos.
                  </video>
                  <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(10,10,18,0.85)', border: '1px solid rgba(74,222,128,0.5)', borderRadius: 10, px: 1.5, py: 0.6, backdropFilter: 'blur(8px)' }}>
                    <CheckCircleIcon sx={{ color: '#4ade80', fontSize: 14 }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#4ade80', letterSpacing: '0.08em', textTransform: 'uppercase' }}>No Alert</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, flexGrow: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: '#f0f0f8', mb: 1.5 }}>
                    Child cries — worker responds immediately
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, mb: 2 }}>
                    A toddler's building blocks fall apart and he begins crying. A nearby worker notices immediately and comes to comfort him. SMCLA detects the distress — and also detects the caregiver's prompt response. <Box component="span" sx={{ color: '#4ade80', fontWeight: 600 }}>No alert is sent.</Box>
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'rgba(74,222,128,0.06)', border: '1px solid rgba(74,222,128,0.15)', borderLeft: '3px solid #4ade80', borderRadius: 2 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                      <Box component="span" sx={{ color: '#4ade80', fontWeight: 700 }}>Why no alert: </Box>
                      SMCLA measures both the child's distress signal and caregiver proximity and engagement. When a worker responds within the expected window, the system recognizes healthy, attentive care — exactly the false-positive problem other systems get wrong.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* Video 2 — Unsupervised children, alert triggered */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.2)', borderRadius: 3, overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', ...fadeStyle(productDemoSection.inView, 0.1) }}>
                <Box sx={{ position: 'relative' }}>
                  <video
                    src="/high_resolution_daycare_outdoo.mp4"
                    controls
                    muted
                    playsInline
                    style={{ width: '100%', maxHeight: 320, objectFit: 'cover', display: 'block', bgcolor: '#0a0a12' } as React.CSSProperties}
                  >
                    Your browser does not support embedded videos.
                  </video>
                  <Box sx={{ position: 'absolute', top: 12, left: 12, display: 'inline-flex', alignItems: 'center', gap: 0.75, bgcolor: 'rgba(10,10,18,0.85)', border: '1px solid rgba(255,107,107,0.5)', borderRadius: 10, px: 1.5, py: 0.6, backdropFilter: 'blur(8px)' }}>
                    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#ff6b6b', boxShadow: '0 0 6px #ff6b6b', animation: 'pulse 1.5s infinite' }} />
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 11, color: '#ff6b6b', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Alert Triggered</Typography>
                  </Box>
                </Box>
                <Box sx={{ p: 3, flexGrow: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 17, color: '#f0f0f8', mb: 1.5 }}>
                    Children outdoors — no caregiver in sight
                  </Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7, mb: 2 }}>
                    Multiple children are active in an outdoor play area. No daycare worker is visible anywhere in the zone. SMCLA begins a timer the moment caregiver presence drops to zero. After the threshold window passes, <Box component="span" sx={{ color: '#ff6b6b', fontWeight: 600 }}>an alert is sent instantly to the facility owner.</Box>
                  </Typography>
                  <Box sx={{ p: 2, bgcolor: 'rgba(255,107,107,0.06)', border: '1px solid rgba(255,107,107,0.15)', borderLeft: '3px solid #ff6b6b', borderRadius: 2 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.65 }}>
                      <Box component="span" sx={{ color: '#ff6b6b', fontWeight: 700 }}>Why it alerts: </Box>
                      Unsupervised children in an outdoor space is a regulatory and safety violation. SMCLA tracks caregiver-to-child coverage continuously — not just when something goes wrong. The alert arrives before any incident occurs.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* See more link */}
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="text" onClick={() => { trackEvent('cta_click', 'See more examples', pathname); navigate('/examples'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See more examples →
            </Button>
          </Box>

          {/* Bottom context strip */}
          <Box sx={{ mt: 2, p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 3, display: 'flex', gap: 2.5, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#5ce1e6' }} />
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 13, color: '#5ce1e6' }}>How SMCLA decides</Typography>
            </Box>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.7, flex: 1, minWidth: 260 }}>
              Every alert weighs distress signals, caregiver proximity, response time, and duration together — not any single trigger in isolation. A crying child with an attentive worker is healthy care. A crying child ignored for minutes is a safeguarding failure. Children left without supervision — even for a short period — is an immediate safety risk. SMCLA knows which is which.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── Operational Intelligence ── */}
      <Box ref={opsSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(opsSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>Operational Intelligence</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            Safety is the floor.<br />Efficiency is the ceiling.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            SMCLA monitors your facility 24/7. That same data tells you when to add staff, which rooms need more coverage, and which workers may be burning out — before it becomes your problem.
          </Typography>
          <Grid container spacing={2.5} sx={{ mb: 4 }}>
            {[
              { icon: <TrendingUpIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Peak Hours Detection', body: 'AI maps exactly when your rooms fill up and when coverage thins — so you schedule proactively, not reactively.' },
              { icon: <PeopleIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Staff Ratio Monitoring', body: 'Real-time alerts when child-to-staff ratios approach regulatory limits. Know before an inspector does.' },
              { icon: <CalendarMonthIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Smarter Scheduling', body: 'Historical pattern data turns gut-feel scheduling into data-driven decisions. Reduce overtime. Eliminate gaps.' },
              { icon: <FavoriteOutlinedIcon sx={{ color: '#5ce1e6', fontSize: 22 }} />, title: 'Worker Wellbeing', body: 'Behavioral drift detection flags early signs of burnout — before a great worker hands in their notice.' },
            ].map(({ icon, title, body }) => (
              <Grid key={title} size={{ xs: 12, sm: 6, md: 3 }}>
                <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2.5, height: '100%' }}>
                  <Box sx={{ display: 'inline-flex', p: 1, bgcolor: 'rgba(92,225,230,0.08)', borderRadius: 1.5, mb: 1.75 }}>{icon}</Box>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="text" onClick={() => { trackEvent('cta_click', 'Learn how it works for your team', pathname); navigate('/for-staff'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              Learn how it works for your team →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Competitive Comparison ── */}
      <Box ref={compareSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', borderBottom: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(compareSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>Why Not Just Cameras?</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            Nothing else comes close.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, mb: 5, maxWidth: 480, mx: 'auto' }}>
            Generic cameras record. Other platforms log or store footage. SMCLA is the only behavioral AI trained specifically on childcare abuse detection.
          </Typography>
          {/* Desktop table */}
          <Box sx={{ overflowX: 'auto', display: { xs: 'none', md: 'block' } }}>
            <Box sx={{ minWidth: 400 }}>
              <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 0.75, mb: 0.75 }}>
                <Box />
                {['SMCLA', 'Others'].map((col, i) => (
                  <Box key={col} sx={{ p: 1.25, textAlign: 'center', bgcolor: i === 0 ? 'rgba(92,225,230,0.08)' : '#13131f', border: i === 0 ? '1px solid rgba(92,225,230,0.2)' : '1px solid rgba(255,255,255,0.06)', borderRadius: 1.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 12, color: i === 0 ? '#5ce1e6' : '#9499b0' }}>{col}</Typography>
                  </Box>
                ))}
              </Box>
              {compareRows.map(({ feature, smcla, others }, rowIdx) => (
                <Box key={feature} sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 0.75, mb: 0.75 }}>
                  <Box sx={{ p: 1.25, display: 'flex', alignItems: 'center', bgcolor: rowIdx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent', borderRadius: 1.5 }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#d0d0e0' }}>{feature}</Typography>
                  </Box>
                  {[smcla, others].map((val, i) => (
                    <Box key={i} sx={{ p: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: i === 0 ? 'rgba(92,225,230,0.04)' : (rowIdx % 2 === 0 ? 'rgba(255,255,255,0.02)' : 'transparent'), border: i === 0 ? '1px solid rgba(92,225,230,0.1)' : 'none', borderRadius: 1.5 }}>
                      <CompareCell value={val} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
          {/* Mobile card view */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            <Stack spacing={1}>
              {compareRows.map(({ feature, smcla, others }) => (
                <Box key={feature} sx={{ p: 2, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#f0f0f8', mb: 1.25 }}>{feature}</Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    {[['SMCLA', smcla, true], ['Others', others, false]].map(([name, val, highlight]) => (
                      <Box key={name as string} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, bgcolor: highlight ? 'rgba(92,225,230,0.08)' : 'rgba(255,255,255,0.04)', border: highlight ? '1px solid rgba(92,225,230,0.2)' : '1px solid rgba(255,255,255,0.06)', borderRadius: 1 }}>
                        <CompareCell value={val as CellValue} />
                        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: highlight ? '#5ce1e6' : '#9499b0', fontWeight: 600 }}>{name as string}</Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button variant="text" onClick={() => { trackEvent('cta_click', 'See the full comparison', pathname); navigate('/compare'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See the full comparison →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── Incident Investigation ── */}
      <Box ref={investigationSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(investigationSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#ff9f43', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>Incident Investigation</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            From alert to full investigation<br />in one click.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, mb: 7, maxWidth: 520, mx: 'auto', lineHeight: 1.65 }}>
            Real-time detection catches the incident. Investigation tells you the full story — every camera, every angle, every moment before it happened.
          </Typography>
          <Grid container spacing={3} sx={{ mb: 5 }}>
            {[
              {
                accent: '#5ce1e6',
                title: '24-Hour Rolling Buffer',
                body: 'Every camera in your facility retains footage continuously for 24 hours. The moment an investigation is opened, nothing is lost — the full context is already preserved.',
              },
              {
                accent: '#8c52ff',
                title: 'Multi-Camera Reconstruction',
                body: 'When an alert is confirmed, SMCLA automatically pulls footage from all cameras — not just the one that triggered. See what happened in every room before, during, and after the incident.',
              },
              {
                accent: '#ff9f43',
                title: 'Forensic-Grade Evidence Package',
                body: 'The investigation record includes tamper-evident clips, chain-of-custody metadata, full audit trail, and timestamped reviewer notes — structured for law enforcement, licensing boards, and legal proceedings.',
              },
            ].map(({ accent, title, body }) => (
              <Grid key={title} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3.5, bgcolor: '#13131f', border: `1px solid ${accent}20`, borderTop: `3px solid ${accent}`, borderRadius: 3, height: '100%' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: accent, mb: 1.5 }}>{title}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ p: { xs: 3, md: 4 }, bgcolor: '#0d0d18', border: '1px solid rgba(255,159,67,0.15)', borderRadius: 3, display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <ManageSearchIcon sx={{ color: '#ff9f43', fontSize: 32, flexShrink: 0, mt: 0.25 }} />
            <Box>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: 15, color: '#f0f0f8', mb: 1 }}>
                How it works: Reviewer confirms True Incident → investigation created automatically
              </Typography>
              <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>
                No manual setup. The moment a reviewer marks an alert as a true incident, SMCLA creates a timestamped investigation record, locks the 24-hour footage buffer, and surfaces the full multi-camera reconstruction for the owner. The complete package — clips, metadata, audit log — is ready to export to law enforcement, CPS, or legal counsel.
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ── Privacy & Trust ── */}
      <Box ref={trustSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(trustSection.inView) }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="flex-start">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: 'inline-flex', p: 2.5, bgcolor: 'rgba(92,225,230,0.08)', border: '1px solid rgba(92,225,230,0.15)', borderRadius: '50%', mb: 3, boxShadow: '0 0 40px rgba(92,225,230,0.12)' }}>
                <ShieldIcon sx={{ color: '#5ce1e6', fontSize: 40 }} />
              </Box>
              <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1 }}>Privacy & Compliance</Typography>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2 }}>Built on Trust</Typography>
              <Typography sx={{ color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, lineHeight: 1.7, mb: 2 }}>
                Audio and video analysis together catch what neither can alone. Audio is used for emotional abuse detection — processed responsibly, with state consent compliance handled during setup.
              </Typography>
              <Button variant="text" onClick={() => { trackEvent('cta_click', 'How we compare to competitors', pathname); navigate('/compare'); }} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                How we compare to competitors →
              </Button>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Stack spacing={2.5}>
                {trustItems.map(({ title, body }, i) => (
                  <Box key={title} sx={{ display: 'flex', gap: 2, ...fadeStyle(trustSection.inView, i * 0.1) }}>
                    <CheckCircleIcon sx={{ color: '#5ce1e6', mt: 0.3, flexShrink: 0, fontSize: 20 }} />
                    <Box>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 15, color: '#f0f0f8', mb: 0.5 }}>{title}</Typography>
                      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 400, fontSize: 14, color: '#9499b0', lineHeight: 1.65 }}>{body}</Typography>
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Testimonial Quote ── */}
      <Box ref={quoteSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, textAlign: 'center', position: 'relative', overflow: 'hidden', ...fadeStyle(quoteSection.inView) }}>
        <Typography sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -55%)', fontFamily: 'Georgia, serif', fontSize: { xs: 200, md: 300 }, lineHeight: 1, color: 'rgba(92,225,230,0.04)', pointerEvents: 'none', userSelect: 'none' }}>"</Typography>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontStyle: 'italic', fontSize: { xs: 20, md: 26 }, color: '#d0d0e0', lineHeight: 1.5, mb: 3 }}>
            "This technology turns our safety commitment into a selling point."
          </Typography>
          <Typography sx={{ fontFamily: 'NowAlt, system-ui, sans-serif', fontWeight: 500, fontSize: 13, color: '#9499b0', letterSpacing: '0.04em' }}>
            — Early Adopter, Multi-Site Daycare Operator
          </Typography>
        </Container>
      </Box>

      {/* ── Parent Motivation Strip ── */}
      <Box ref={parentSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(parentSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>The Enrollment Advantage</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            Parents choose the safest daycare.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, mb: 6, maxWidth: 480, mx: 'auto' }}>
            Safety is now the #1 driver of daycare enrollment decisions. When you deploy SMCLA, it becomes your competitive advantage and your best marketing.
          </Typography>
          <Grid container spacing={3}>
            {[
              { stat: '50%', label: 'of parents rank safety as their top daycare criterion', sub: '+18% increase since 2022 — growing fast' },
              { stat: '47%', label: 'say caregiver vigilance is their #1 deciding factor', sub: 'More than location, cost, or curriculum' },
              { stat: '75%', label: 'believe childcare workers should be government-licensed', sub: 'Parents expect the same accountability as other professionals' },
            ].map(({ stat, label, sub }) => (
              <Grid key={stat} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(92,225,230,0.1)', borderRadius: 3, height: '100%', textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.8rem', color: '#5ce1e6', lineHeight: 1, mb: 1 }}>{stat}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5 }}>{sub}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="text" onClick={() => navigate('/parents')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#5ce1e6', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See the full parent trust data →
            </Button>
          </Box>
          <Typography sx={{ textAlign: 'center', mt: 1, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(148,153,176,0.5)' }}>
            Source: KinderCare 2025 Parent Confidence Index, NAEYC & MomsRising Survey
          </Typography>
        </Container>
      </Box>

      {/* ── Why SMC ── */}
      <Box ref={whySection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(whySection.inView) }}>
        <Container maxWidth="md">
          <Grid container spacing={6} alignItems="center">
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid rgba(92,225,230,0.15)', boxShadow: '0 0 60px rgba(92,225,230,0.1)' }}>
                <img src="/infographic-latest.jpeg" alt="Why Smart Media Clarity" style={{ display: 'block', width: '100%', height: 'auto', objectFit: 'cover' }} />
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 7 }}>
              <Typography variant="overline" sx={{ display: 'block', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1 }}>Why It Matters</Typography>
              <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.2rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 4 }}>
                Protect your reputation before a crisis hits
              </Typography>
              <Stack spacing={2}>
                {whyItems.map((item, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, p: 2, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderLeft: '3px solid #5ce1e6', borderRadius: 2, ...fadeStyle(whySection.inView, i * 0.1) }}>
                    <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 500, fontSize: 14, color: '#d0d0e0', lineHeight: 1.6 }}>{item}</Typography>
                  </Box>
                ))}
              </Stack>
              <Button variant="text" onClick={() => navigate('/why')} sx={{ mt: 3, color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, p: 0, '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
                Read the real case studies →
              </Button>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* ── Financial Risk Section ── */}
      <Box ref={riskSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(riskSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#ff6b6b', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>The Financial Reality</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 1.5, letterSpacing: '-0.02em' }}>
            One incident can cost you everything.
          </Typography>
          <Typography sx={{ textAlign: 'center', color: '#9499b0', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 15, mb: 6, maxWidth: 480, mx: 'auto' }}>
            These are real settlements from 2021–2025. Every one was preventable. SMCLA costs less than one hour of attorney time.
          </Typography>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {[
              { amount: '$16M', label: 'SC infant death settlement', year: '2024' },
              { amount: '$262K', label: 'Average physical abuse case', year: 'NC 2024' },
              { amount: '51', label: 'Criminal counts — one Colorado worker', year: '2025', noSymbol: true },
            ].map(({ amount, label, year }) => (
              <Grid key={label} size={{ xs: 12, md: 4 }}>
                <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderRadius: 3, textAlign: 'center' }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: '2.5rem', color: '#ff6b6b', lineHeight: 1, mb: 0.5 }}>{amount}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.5 }}>{label}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#5ce1e6' }}>{year}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center' }}>
            <Button variant="text" onClick={() => navigate('/reputation')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 13, color: '#ff6b6b', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}>
              See the full financial risk breakdown →
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── FAQ ── */}
      <Box ref={faqSection.ref} sx={{ bgcolor: '#0d0d18', borderTop: '1px solid rgba(255,255,255,0.06)', py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(faqSection.inView) }}>
        <Container maxWidth="md">
          <Typography variant="overline" sx={{ display: 'block', textAlign: 'center', color: '#5ce1e6', fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, letterSpacing: '0.12em', mb: 1.5 }}>Common Questions</Typography>
          <Typography variant="h3" sx={{ textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.5rem' }, color: '#f0f0f8', mb: 6, letterSpacing: '-0.02em' }}>
            Everything you need to know
          </Typography>
          <Stack spacing={1}>
            {faqItems.map(({ q, a }) => (
              <Accordion key={q} elevation={0} sx={{ bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px !important', '&:before': { display: 'none' }, '&.Mui-expanded': { border: '1px solid rgba(92,225,230,0.2)' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: '#5ce1e6' }} />} sx={{ px: 3, py: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: { xs: 14, md: 16 }, color: '#f0f0f8' }}>{q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 3, pt: 0, pb: 2.5 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', lineHeight: 1.7 }}>{a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ── Book a Demo ── */}
      <Box ref={demoSection.ref} sx={{ py: { xs: 8, md: 12 }, px: 3, ...fadeStyle(demoSection.inView) }}>
        <Container maxWidth="sm" ref={contactFormRef}>
          <ContactForm pagePath={pathname} />
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}

// ── Scroll to top + page engagement tracking on route change ─────────────────
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname);
    setVisitorTypeDimension();
    incrementSessionDepth();

    // Scroll depth — fires once per milestone per page
    const fired = new Set<number>();
    const onScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = Math.round((window.scrollY / scrollable) * 100);
      for (const m of [25, 50, 75, 90]) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackScrollDepth(m, pathname);
        }
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // Time on page — fires at 30s, 60s, 120s milestones
    const timers = [
      setTimeout(() => trackTimeOnPage(30, pathname), 30_000),
      setTimeout(() => trackTimeOnPage(60, pathname), 60_000),
      setTimeout(() => trackTimeOnPage(120, pathname), 120_000),
    ];

    return () => {
      window.removeEventListener('scroll', onScroll);
      timers.forEach(clearTimeout);
    };
  }, [pathname]);
  return null;
}

// ── App root ──────────────────────────────────────────────────────────────────
function App() {
  return (
    <Router>
      <CookieConsentBanner />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/why" element={<Why />} />
        <Route path="/contact" element={<ContactUsPage />} />
        <Route path="/reputation" element={<ReputationPage />} />
        <Route path="/parents" element={<ParentsPage />} />
        <Route path="/compare" element={<ComparePage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/cost-of-one-bad-hire" element={<BlogCostPage />} />
        <Route path="/blog/cameras-not-enough" element={<BlogCamerasPage />} />
        <Route path="/blog/how-to-prevent-daycare-abuse" element={<BlogPreventPage />} />
        <Route path="/blog/emotional-abuse-detection" element={<BlogEmotionalAbusePage />} />
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/insurance-partners" element={<InsurancePartnerPage />} />
        <Route path="/use-cases" element={<UseCasesPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/blog/punkin-doodles-134-incidents" element={<BlogGuziejkaPage />} />
        <Route path="/blog/suny-cortland-audio-detection" element={<BlogSunyCortlandPage />} />
        <Route path="/blog/des-plaines-mothers-touch" element={<BlogDesPlainePage />} />
        <Route path="/blog/rocking-horse-ranch-harvey-mucklebust" element={<BlogHarveyMucklebustPage />} />
        <Route path="/blog/disputed-allegations-rainbow-childcare" element={<BlogDisputedAllegationsPage />} />
        <Route path="/blog/sexual-predators-daycare-prevention" element={<BlogSexualPredatorsPage />} />
        <Route path="/blog/arizona-coolidge-daycare-lawsuit" element={<BlogArizonaCoolidgePage />} />
        <Route path="/examples" element={<ExamplesPage />} />
        <Route path="/integrations" element={<IntegrationsPage />} />
        <Route path="/government" element={<GovernmentPage />} />
        <Route path="/camera-partners" element={<CameraPartnersPage />} />
        <Route path="/lp/daycare-owner" element={<LpDaycareOwner />} />
        <Route path="/lp/enterprise-coo" element={<LpEnterpriseCOO />} />
        <Route path="/lp/insurance" element={<LpInsurance />} />
        <Route path="/lp/government-minister" element={<LpGovernment />} />
        <Route path="/lp/camera-partner" element={<LpCameraPartner />} />
        <Route path="/lp/app-partner" element={<LpAppPartner />} />
        <Route path="/for-staff" element={<ForStaffPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      </Routes>
    </Router>
  );
}

export default App;
